import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CollapsibleFormSection } from '../../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../../components/ui/DeleteIconButton';
import { DuplicateIconButton } from '../../../components/ui/DuplicateIconButton';
import { EditIconButton } from '../../../components/ui/EditIconButton';
import { FormActionBar } from '../../../components/ui/FormActionBar';
import { InlineAlert } from '../../../components/ui/InlineAlert';
import { ViewIconLink } from '../../../components/ui/ViewIconLink';
import { useMessageBox } from '../../../contexts/MessageBoxContext';
import { useCollapsibleItemForm } from '../../../hooks/useCollapsibleItemForm';
import {
  EVALUATION_FORM_TYPE_OPTIONS,
  EvaluationCycle,
  EvaluationForm,
  EvaluationFormType,
  EvaluationFormUpsertPayload,
  createEvaluationForm,
  deleteEvaluationForm,
  duplicateEvaluationForm,
  listEvaluationForms,
  updateEvaluationForm,
} from '../../../services/peoplePerformanceApi';

function audienceLabel(value?: string | null): string {
  if (value === 'Colaborador' || value === 'Gestor' || value === 'Executivo') {
    return value;
  }
  return value?.trim() || '—';
}

function formTypeLabel(value?: string | null): string {
  return EVALUATION_FORM_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

type EvaluationCycleFormsSectionProps = {
  cycleId: string;
  cycle: EvaluationCycle | null;
  canWrite: boolean;
  onCountChange?: (count: number) => void;
};

export function EvaluationCycleFormsSection({
  cycleId,
  cycle,
  canWrite,
  onCountChange,
}: EvaluationCycleFormsSectionProps) {
  const { confirm, confirmDelete } = useMessageBox();
  const {
    formExpanded,
    expandForm,
    collapseForm,
    handleCreateSuccess,
    toggleOrCancel,
  } = useCollapsibleItemForm();
  const [forms, setForms] = useState<EvaluationForm[]>([]);
  const [loading, setLoading] = useState(true);
  const emptyForm: EvaluationFormUpsertPayload = {
    name: '',
    formType: 'Self',
    audience: 'Colaborador',
  };
  const [evaluationForm, setEvaluationForm] = useState<EvaluationFormUpsertPayload>(emptyForm);
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [savingForm, setSavingForm] = useState(false);
  const [duplicatingFormId, setDuplicatingFormId] = useState<string | null>(null);

  const canEdit = canWrite && cycle?.status === 'Draft';

  function availableFormTypes(): { value: EvaluationFormType; label: string }[] {
    return EVALUATION_FORM_TYPE_OPTIONS.filter((option) => {
      if (option.value === 'Self') return !!cycle?.enableSelfEvaluation;
      if (option.value === 'Manager') return !!cycle?.enableManagerEvaluation;
      if (option.value === 'Peers') return !!cycle?.enablePeersEvaluation;
      if (option.value === 'Upward') return !!cycle?.enableUpwardEvaluation;
      return false;
    });
  }

  async function loadForms(): Promise<void> {
    setLoading(true);
    try {
      const result = await listEvaluationForms(cycleId);
      setForms(result);
      onCountChange?.(result.length);
    } catch {
      toast.error('Falha ao carregar formulários.');
      setForms([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  function resetEvaluationFormState(): void {
    setEditingFormId(null);
    const defaultType: EvaluationFormType = cycle?.enableSelfEvaluation
      ? 'Self'
      : cycle?.enableManagerEvaluation
        ? 'Manager'
        : cycle?.enablePeersEvaluation
          ? 'Peers'
          : 'Upward';
    setEvaluationForm({ name: '', formType: defaultType, audience: 'Colaborador' });
  }

  function startEditEvaluationForm(item: EvaluationForm): void {
    expandForm();
    setEditingFormId(item.id);
    setEvaluationForm({
      name: item.name,
      formType: item.formType,
      audience: item.audience,
      sortOrder: item.sortOrder,
    });
  }

  async function handleSaveEvaluationForm(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canEdit) return;
    setSavingForm(true);
    try {
      if (editingFormId) {
        await updateEvaluationForm(cycleId, editingFormId, evaluationForm);
        toast.success('Formulário atualizado.');
        resetEvaluationFormState();
      } else {
        await createEvaluationForm(cycleId, evaluationForm);
        toast.success('Formulário criado.');
        handleCreateSuccess(resetEvaluationFormState);
      }
      await loadForms();
    } catch {
      toast.error('Falha ao salvar formulário.');
    } finally {
      setSavingForm(false);
    }
  }

  async function handleDuplicateEvaluationForm(id: string): Promise<void> {
    if (!canEdit) return;
    const item = forms.find((current) => current.id === id);
    if (
      !(await confirm({
        title: 'Duplicar formulário',
        message: item
          ? `Duplicar "${item.name}"? As perguntas selecionadas serão clonadas.`
          : 'Duplicar este formulário?',
        confirmLabel: 'Duplicar',
      }))
    ) {
      return;
    }

    setDuplicatingFormId(id);
    try {
      await duplicateEvaluationForm(cycleId, id);
      toast.success('Formulário duplicado.');
      await loadForms();
    } catch {
      toast.error('Falha ao duplicar formulário.');
    } finally {
      setDuplicatingFormId(null);
    }
  }

  async function handleDeleteEvaluationForm(id: string): Promise<void> {
    if (!canEdit) return;
    const item = forms.find((current) => current.id === id);
    if (
      !(await confirmDelete({
        title: 'Excluir formulário',
        message: item ? `Excluir o formulário "${item.name}"?` : 'Excluir este formulário?',
      }))
    ) {
      return;
    }

    try {
      await deleteEvaluationForm(cycleId, id);
      toast.success('Formulário excluído.');
      if (editingFormId === id) {
        collapseForm(resetEvaluationFormState);
      }
      await loadForms();
    } catch {
      toast.error('Falha ao excluir formulário.');
    }
  }

  return (
    <section className="mb-4 rounded-xl border border-secondary-200 bg-white p-4">
      <div className="mb-3">
        <h2 className="text-lg font-semibold text-secondary-900">Formulários de avaliação</h2>
        <p className="text-sm text-secondary-500">
          Defina formulários por tipo (autoavaliação, gestor 90°, pares 180° ou colaborador→gestor) e nível do avaliado. No detalhe, selecione as perguntas do catálogo.
        </p>
      </div>

      {loading ? (
        <p className="mb-4 text-sm text-secondary-500">Carregando formulários...</p>
      ) : forms.length === 0 ? (
        <InlineAlert className="mb-4">
          Nenhum formulário cadastrado neste ciclo. Use o formulário abaixo para criar o primeiro.
        </InlineAlert>
      ) : (
        <div className="mb-4 overflow-x-auto">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Nível</th>
                <th>Perguntas</th>
                <th className="w-48">Ações</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-secondary-900">{item.name}</td>
                  <td>{formTypeLabel(item.formType)}</td>
                  <td>{audienceLabel(item.audience)}</td>
                  <td>{item.questionCount}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <ViewIconLink
                        to={`/people/evaluation-cycles/${cycleId}/forms/${item.id}`}
                        label="Detalhes do formulário"
                      />
                      {canEdit ? (
                        <>
                          <EditIconButton
                            label="Editar formulário"
                            onClick={() => startEditEvaluationForm(item)}
                          />
                          <DuplicateIconButton
                            label={duplicatingFormId === item.id ? 'Duplicando...' : 'Duplicar formulário'}
                            disabled={duplicatingFormId === item.id}
                            onClick={() => void handleDuplicateEvaluationForm(item.id)}
                          />
                          <DeleteIconButton
                            label="Excluir formulário"
                            onClick={() => void handleDeleteEvaluationForm(item.id)}
                          />
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && availableFormTypes().length > 0 ? (
        <CollapsibleFormSection
          title={editingFormId ? 'Editar formulário' : 'Novo formulário'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetEvaluationFormState)}
          isEditing={!!editingFormId}
          createSummary="Clique para expandir e cadastrar um novo formulário"
          className="mt-4 mb-0"
        >
          <form onSubmit={(event) => void handleSaveEvaluationForm(event)} className="erp-form px-4 pb-4 pt-3">
            <p className="mb-3 text-sm text-secondary-500">
              Cada formulário é único por tipo e nível (ex.: autoavaliação de colaborador ≠ autoavaliação de gestor).
            </p>
            <div className="erp-form-grid">
              <label className="erp-label sm:col-span-2">
                Nome
                <input
                  className="erp-input"
                  value={evaluationForm.name}
                  onChange={(event) => setEvaluationForm({ ...evaluationForm, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Tipo de avaliação
                <select
                  className="erp-input"
                  value={evaluationForm.formType}
                  onChange={(event) =>
                    setEvaluationForm({
                      ...evaluationForm,
                      formType: event.target.value as EvaluationFormType,
                    })
                  }
                  required
                >
                  {availableFormTypes().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Nível do avaliado
                <select
                  className="erp-input"
                  value={evaluationForm.audience || 'Colaborador'}
                  onChange={(event) =>
                    setEvaluationForm({
                      ...evaluationForm,
                      audience: event.target.value,
                    })
                  }
                  required
                >
                  <option value="Colaborador">Colaborador</option>
                  <option value="Gestor">Gestor</option>
                  <option value="Executivo">Executivo</option>
                </select>
              </label>
            </div>
            <FormActionBar
              onCancel={() => collapseForm(resetEvaluationFormState)}
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={savingForm}>
                {savingForm ? 'Salvando...' : editingFormId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}
    </section>
  );
}
